import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ScanLine, CalendarDays, FileBarChart2,
  BellRing, ShieldCheck, Sparkles, Settings, LogOut, Moon, Sun,
  ClipboardList,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager"] },
  { to: "/employees", label: "Personel", icon: Users, roles: ["admin", "manager"] },
  { to: "/terminal", label: "QR Terminal", icon: ScanLine, roles: ["admin", "manager"] },
  { to: "/calendar", label: "Takvim", icon: CalendarDays, roles: ["admin", "manager"] },
  { to: "/leaves", label: "İzinler", icon: ClipboardList, roles: ["admin", "manager"] },
  { to: "/reports", label: "Raporlar", icon: FileBarChart2, roles: ["admin", "manager"] },
  { to: "/notifications", label: "Bildirimler", icon: BellRing, roles: ["admin", "manager"] },
  { to: "/ai-insights", label: "AI Analiz", icon: Sparkles, roles: ["admin", "manager"] },
  { to: "/security", label: "Güvenlik", icon: ShieldCheck, roles: ["admin"] },
  { to: "/settings", label: "Ayarlar", icon: Settings, roles: ["admin"] },
];

/** Shared sidebar content used both in the desktop static sidebar
 *  and the mobile drawer (Sheet). */
export function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="flex flex-col h-full">
      <div className="mb-6 flex items-center gap-3">
        <img src="/logo/qrpts-logo.png" alt="QRPTS" className="w-11 h-11 object-contain" data-testid="app-logo" />
        <div>
          <div className="font-display font-bold text-base tracking-tight">QRPTS</div>
          <div className="text-xs text-muted-foreground">Personel Takip</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="mt-4 pt-4 border-t border-border/60 space-y-2 shrink-0">
        <div className="flex items-center justify-between px-2 py-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || user?.email}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{user?.role}</div>
          </div>
          <Button
            data-testid="theme-toggle"
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="rounded-full"
            aria-label="tema"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
        </div>
        <Button
          data-testid="logout-btn"
          variant="outline"
          className="w-full justify-start gap-2 rounded-lg"
          onClick={async () => {
            await logout();
            onNavigate?.();
            nav("/login");
          }}
        >
          <LogOut className="w-4 h-4" /> Çıkış Yap
        </Button>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside
      className="glass-panel w-64 shrink-0 h-screen sticky top-0 hidden md:flex flex-col p-5 border-r border-border/60"
      data-testid="sidebar"
    >
      <SidebarContent />
    </aside>
  );
}
